import { api as wiredApi } from './index';

/**
 * A rider's work on InGo's API (the mobile plan's M5; InGo ADR 0016, 0018,
 * 0020): going on and off duty with the position heartbeat, the live offers,
 * the job they carry, and the job's moves (giving it back before pickup, the
 * pickup with the package's photo, the drop-off on the client's code). The
 * rider is the token: no id is ever sent for them.
 *
 * Built from its client so a test can hand it a fake, as `riders.js` is;
 * `riderWork` below is the app's wired copy.
 */
export function createRiderWork({ api }) {
  return {
    /** `GET rider/me/duty`: the switch, whether the platform counts the rider online, how often to heartbeat. */
    async duty() {
      const { data } = await api.get('/rider/me/duty');

      return data;
    },

    /**
     * `PUT rider/me/duty`. On, it is refused (`RIDER_CANNOT_WORK`, with the
     * sentence to show) for a rider the office has not approved or has
     * suspended.
     */
    async setDuty(onDuty) {
      const { data } = await api.put('/rider/me/duty', { on_duty: Boolean(onDuty) });

      return data;
    },

    /**
     * `POST rider/me/presence`: where the rider is, with the phone's own time.
     * Refused `NOT_ON_DUTY` when the platform ended the shift (the app must
     * show the switch off, not keep sending).
     */
    async heartbeat(fix) {
      const { data } = await api.post('/rider/me/presence', {
        latitude: fix.latitude,
        longitude: fix.longitude,
        accuracy_metres: fix.accuracyMetres ?? null,
        heading_degrees: fix.headingDegrees ?? null,
        speed_kph: fix.speedKph ?? null,
        recorded_at: fix.recordedAt,
      });

      return data;
    },

    /** `GET rider/me/offers`: the live offers, the soonest to lapse first. */
    async offers() {
      const { data } = await api.get('/rider/me/offers');

      return Array.isArray(data) ? data : [];
    },

    /**
     * `POST rider/me/offers/{offer}/acceptance`. Refused, never retried:
     * `OFFER_NO_LONGER_OPEN`, `DELIVERY_TAKEN`, `RIDER_BUSY`.
     */
    async accept(offerId) {
      const { data } = await api.post(`/rider/me/offers/${offerId}/acceptance`, {});

      return data;
    },

    /** `POST rider/me/offers/{offer}/decline`: the next riders are asked at once. */
    async decline(offerId, reason = null) {
      const { data } = await api.post(`/rider/me/offers/${offerId}/decline`, reason ? { reason } : {});

      return data;
    },

    /** `GET rider/me/deliveries/current`: the job carried now, or null. */
    async current() {
      const { data } = await api.get('/rider/me/deliveries/current');

      return data ?? null;
    },

    /** `GET rider/me/deliveries`: every job carried, newest first (one page). */
    async deliveries() {
      const { data } = await api.get('/rider/me/deliveries');

      return Array.isArray(data) ? data : [];
    },

    /** `GET rider/me/deliveries/{id}`: only the rider's own; another's is a 404. */
    async delivery(id) {
      const { data } = await api.get(`/rider/me/deliveries/${id}`);

      return data;
    },

    /** Before pickup only; the reason is one of the app's own list (`HAND_BACK_REASONS`). */
    async handBack(id, reason, note = null) {
      const { data } = await api.post(`/rider/me/deliveries/${id}/hand-back`, note ? { reason, note } : { reason });

      return data;
    },

    /** The pickup, proved by the package's photo (an image Blob or File, required). */
    async pickUp(id, photo, fileName = 'package.jpg') {
      const form = new FormData();
      form.append('photo', photo, fileName);

      const { data } = await api.post(`/rider/me/deliveries/${id}/pickup`, undefined, { form });

      return data;
    },

    /**
     * The drop-off, on the six digits the client gives at the door. Refused
     * `CODE_WRONG` (the tries left in its sentence) or `CODE_LOCKED` (the
     * office has been told).
     */
    async dropOff(id, code) {
      const { data } = await api.post(`/rider/me/deliveries/${id}/drop-off`, { code });

      return data;
    },
  };
}

export const riderWork = createRiderWork({ api: wiredApi });
